import type { HttpHeaders, Message, RequestError, ResponseHeaders } from '@ps/web-core-api';
import type {
  Currency,
  PEPHIOOptions,
  TournamentHistoryRequestType,
  UpdateAccountDetailsAction
} from '@ps/web-core-enums';
import type { PlayMoneyRefillResult, SecurityQuestion } from '@ps/web-shared-api-types/v0/RAM2';

import type {
  AccountHistoryStatementRequest,
  AccountLocales,
  AccountPayload,
  EffectParams,
  LugasPlayerStatusState,
  Response
} from '../../types';
import type { ErrorCollection } from '../../types/Response';

import type { AccountState } from './account';

export interface HandHistoryPayload {
  requestType: number;
  param?: number;
  handId?: number;
  numRequested?: number;
  locale: number;
}

export interface CasinoGameHistoryPayload {
  requestType: string;
  numOfHands?: number;
  numOfSeconds?: number;
  handId?: number;
  locale: number;
  sendByEmail?: boolean;
}

const catchError = async (
  options: Parameters<EffectParams['request']>['0'],
  { request }: EffectParams
): Promise<Response> => {
  try {
    const response = await request(options);
    return { ...(response.body as object), success: true };
  } catch (err) {
    const error = err as RequestError<ErrorCollection>;
    return {
      statusCode: (error?.headers as ResponseHeaders)?.statusCode,
      success: false,
      message: error?.body?.message,
      errors: error.body as ErrorCollection
    };
  }
};

const effects: {
  fetch(_payload: void, params: EffectParams): Promise<void>;
  requestHandHistory(payload: HandHistoryPayload, params: EffectParams): Promise<Response>;
  requestCasinoHandHistory(
    payload: CasinoGameHistoryPayload,
    params: EffectParams
  ): Promise<Response>;
  requestPlayingHistory(
    payload: {
      from: string;
      to: string;
      zipPassword: string;
      includeFpp: boolean;
      reportFormat: string;
      password?: string;
    },
    params: EffectParams
  ): Promise<Response>;
  requestTournamentHistory(
    payload: { type: TournamentHistoryRequestType; tournamentId: string },
    params: EffectParams
  ): Promise<Response>;
  updateAccount(
    { fetchAccount, ...payload }: AccountPayload,
    params: EffectParams
  ): Promise<Response>;
  updateLugasCrossOperatorDepositLimit(
    payload: { limitAmount?: number },
    params: EffectParams
  ): Promise<Response>;
  updatePEPHIOSelection(
    payload: { pepHioSelection: PEPHIOOptions; rmf: boolean },
    params: EffectParams
  ): Promise<Response>;
  confirmAccountDetails(
    payload: { action: UpdateAccountDetailsAction },
    params: EffectParams
  ): Promise<Response>;
  updatePokerTimeLimit(
    payload: { amount: number; currency: Currency; type: number; passwordRequired: boolean },
    params: EffectParams
  ): Promise<Response>;
  mobileVerificationCode(payload: { code: number }, params: EffectParams): Promise<Response>;
  resendVerificationCode(_payload: void, params: EffectParams): Promise<Response>;
  validateEmailCode(payload: { code: number }, params: EffectParams): Promise<Response>;
  sendValidationEmail(_payload: void, params: EffectParams): Promise<unknown>;
  validateEmail(
    payload: { locale: string; site: string; country: string; signature: string; message: string },
    params: EffectParams
  ): Promise<Response>;
  updateEmailLocaleSettings(settings: AccountLocales, params: EffectParams): Promise<Response>;
  updatePassword(
    payload: { password: string; oldPassword: string },
    params: EffectParams
  ): Promise<Response>;
  getSecurityQuestions(_payload: void, { request }: EffectParams): Promise<SecurityQuestion[]>;
  updateStrongAuthPassword(
    payload: {
      newPassword: string;
      oldPassword: string;
      answer1: string;
      answer2: string;
      ssn: string;
    },
    params: EffectParams
  ): Promise<Response>;
  updatePasswordReminderFlag(
    payload: { pwdReminder: boolean },
    params: EffectParams
  ): Promise<Response>;
  updatePlayChips(
    _payload: void,
    params: EffectParams
  ): Promise<Message<PlayMoneyRefillResult, void, HttpHeaders>>;
  requestAccountHistoryStatement(
    payload: AccountHistoryStatementRequest,
    params: EffectParams
  ): Promise<Response>;
  getLugasStatus(
    _payload: void,
    { request, dispatch }: EffectParams
  ): Promise<LugasPlayerStatusState>;
  sendLugasLongSessionAcknowledgment(
    _payload: void,
    { request, dispatch }: EffectParams
  ): Promise<void>;
  checkZipPassword(payload: { password: string }, params: EffectParams): Promise<Response>;
  fetchVipPoints(_payload: void, params: EffectParams): Promise<Response>;
} = {
  async fetch(_payload, { dispatch, request }) {
    const { body } = await request<Partial<AccountState>>({
      service: '/api/v1-preview/account',
      http: true,
      retry: { count: 2 }
    });
    dispatch.account.update(body);
  },
  requestHandHistory(payload, params) {
    return catchError(
      {
        service: '/api/v1-preview/account/history/client-history',
        body: payload,
        method: 'POST',
        http: true
      },
      params
    );
  },
  requestCasinoHandHistory(payload, params) {
    return catchError(
      {
        service: '/api/v1-preview/account/history/casino-hand',
        body: { ...payload, sendByEmail: true }
      },
      params
    );
  },
  requestPlayingHistory(payload, params) {
    return catchError(
      {
        service: '/api/v1-preview/account/history/audit',
        body: payload,
        method: 'POST',
        http: true
      },
      params
    );
  },
  requestTournamentHistory({ type, tournamentId: tournId }, params) {
    return catchError(
      { service: '/api/v1-preview/account/history/tournament', body: { type, tournId } },
      params
    );
  },
  async updateAccount({ fetchAccount = true, ...payload }, params) {
    const response = await catchError(
      {
        service: '/api/v1-preview/account',
        method: 'PATCH',
        body: payload
      },
      params
    );
    if (fetchAccount && response.success) {
      await params.dispatch.account.fetch();
    }
    return response;
  },
  async updatePEPHIOSelection(payload, params) {
    return await catchError(
      { service: '/api/v1-preview/account/occupation-pep-hio', method: 'POST', body: payload },
      params
    );
  },
  async confirmAccountDetails(payload, params) {
    return await catchError(
      {
        service: '/api/v1-preview/account/details/confirmation-action',
        method: 'POST',
        body: payload
      },
      params
    );
  },
  async updateLugasCrossOperatorDepositLimit(payload, params) {
    return await catchError(
      { service: '/api/v1-preview/account/lugas-registration', method: 'POST', body: payload },
      params
    );
  },
  async updatePokerTimeLimit(payload, params) {
    return await catchError(
      { service: 'responsiblegaming.updategamelimits', body: payload },
      params
    );
  },
  async mobileVerificationCode(payload, params) {
    const response = await catchError(
      { service: '/api/v1-preview/account/mobile-code/validate', method: 'POST', body: payload },
      params
    );
    return response;
  },
  async resendVerificationCode(_payload, params) {
    const response = await catchError(
      { service: '/api/v1-preview/account/mobile-code/resend' },
      params
    );
    return response;
  },
  async validateEmailCode(payload, params) {
    const response = await catchError(
      { service: '/api/v1-preview/account/email-code/validate', method: 'POST', body: payload },
      params
    );
    await params.dispatch.account.fetch();
    const state = params.store.getState();
    if (response && response.success && params.select.device.isMobileEmbedded(state)) {
      window.starsweb?.auth?.reconnect();
    }
    return response;
  },
  sendValidationEmail(_payload, { request }) {
    return request({ service: '/api/v1-preview/account/validation-email', method: 'POST' });
  },
  async validateEmail(payload, params) {
    return catchError(
      { service: '/api/v1-preview/account/activate', method: 'POST', body: payload },
      params
    );
  },
  updateEmailLocaleSettings(settings, params) {
    return catchError(
      { service: '/api/v1-preview/account/locales', method: 'POST', body: settings },
      params
    );
  },
  updatePassword(payload, params) {
    return catchError(
      { service: '/api/v1-preview/auth/password', body: payload, method: 'PUT' },
      params
    );
  },
  async getSecurityQuestions(_payload, { request }) {
    const { body } = await request<SecurityQuestion[]>({
      service: '/api/v1-preview/auth/password/change/security-questions',
      method: 'GET'
    });
    return body;
  },
  updateStrongAuthPassword(payload, params) {
    return catchError(
      { service: '/api/v1-preview/auth/password/change/strongauth', body: payload, method: 'POST' },
      params
    );
  },
  updatePasswordReminderFlag(payload, params) {
    return catchError(
      { service: '/api/v1-preview/account/password-reminder', method: 'POST', body: payload },
      params
    );
  },
  updatePlayChips(_payload, { request }) {
    return request<PlayMoneyRefillResult>({
      service: '/api/v1-preview/account/play-money-refill',
      method: 'POST'
    });
  },
  requestAccountHistoryStatement(payload, params): Promise<Response> {
    return catchError(
      { service: '/api/v1-preview/account/history/statement', method: 'POST', body: payload },
      params
    );
  },
  async getLugasStatus(_payload, { request, dispatch }) {
    const { body } = await request<LugasPlayerStatusState>({
      service: '/api/v1-preview/account/lugas-player-status',
      method: 'GET'
    });
    dispatch.account.update({ lugasPlayerStatus: body });
    return body;
  },
  async sendLugasLongSessionAcknowledgment(_payload: void, { request, dispatch }) {
    await request({
      service: '/api/v1-preview/account/lugas-long-session-ack',
      method: 'POST'
    });
    dispatch.account.getLugasStatus();
  },
  checkZipPassword(payload, params) {
    return catchError({ service: 'responsiblegaming.checkzippassword', body: payload }, params);
  },
  fetchVipPoints(payload, params) {
    return catchError({ service: 'wc3.wallet.get.vipInfo', body: payload }, params);
  }
};

export default effects;
